import { Fragment, useState } from 'react';
import { Typography } from '@mui/material';
import styled from 'styled-components';
import { ReactComponent as EditIcon } from '../../../assets/16x16/edit.svg';
import { capitalizeWords, pluralizeString } from '../../../utils/common';
import Button from '../../Button';
import { Flex } from '../../Flex';
import {
  ApprovalCard,
  ApprovalCardBody,
  ApprovalCardHeader,
  ApprovalCardTitle,
} from '../toolRenderers/approvalCard';
import { isRecord } from '../utils';
import { fontSize, fontWeight, space } from '../../../style/utils';

const MAX_PREVIEW_ROWS = 6;
const MAX_VALUE_LENGTH = 80;

const ChangesGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(100px, max-content) minmax(0, 1fr);
  column-gap: ${space('lg')};
  row-gap: ${space('sm')};
  width: 100%;
`;

const Actions = styled(Flex)`
  align-items: center;
  justify-content: flex-end;
  gap: ${space('sm')};
`;

const parseObject = (value: unknown): Record<string, unknown> | undefined => {
  if (isRecord(value)) {
    return value;
  }
  if (typeof value !== 'string') {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(value);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const getProposedPageUpdate = (
  args?: Record<string, unknown>,
): Record<string, unknown> | undefined => {
  if (!args) {
    return undefined;
  }
  if (args.form !== undefined) {
    return parseObject(args.form);
  }
  const form = Object.fromEntries(
    Object.entries(args).filter(([key]) => key !== 'reason'),
  );
  return Object.keys(form).length > 0 ? form : undefined;
};

const formatFieldLabel = (field: string): string =>
  capitalizeWords(field.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' '));

const clip = (value: string): string =>
  value.length > MAX_VALUE_LENGTH
    ? `${value.slice(0, MAX_VALUE_LENGTH).trimEnd()}…`
    : value;

export const formatPageUpdateValue = (value: unknown): string | undefined => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    const parsed = tryParseJson(value);
    if (parsed !== undefined && typeof parsed !== 'string') {
      return formatPageUpdateValue(parsed);
    }
    return clip(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'None';
    }
    if (value.every((item) => typeof item === 'string')) {
      return clip(value.join(', '));
    }
    return pluralizeString(value.length, 'value', 'values');
  }
  if (isRecord(value)) {
    const nested = Object.entries(value).flatMap(
      ([nestedField, nestedValue]) => {
        const formatted = formatPageUpdateValue(nestedValue);
        return formatted
          ? [`${formatFieldLabel(nestedField)}: ${formatted}`]
          : [];
      },
    );
    return nested.length > 0
      ? clip(nested.join(', '))
      : clip(JSON.stringify(value));
  }
  return String(value);
};

const tryParseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

export const getPageUpdateChangeRows = (
  args?: Record<string, unknown>,
): { field: string; label: string; value: string }[] => {
  const proposed = getProposedPageUpdate(args);
  if (!proposed) {
    return [];
  }
  return Object.entries(proposed).flatMap(([field, value]) => {
    const formatted = formatPageUpdateValue(value);
    if (!formatted) {
      return [];
    }
    return [{ field, label: formatFieldLabel(field), value: formatted }];
  });
};

type Props = {
  rows?: { field: string; label: string; value: string }[];
  args?: Record<string, unknown>;
  isDisabled?: boolean;
  onApply: () => void | Promise<void>;
  onReject: () => void | Promise<void>;
};

const PageUpdateApprovalCard = ({
  rows: previewRows,
  args,
  isDisabled,
  onApply,
  onReject,
}: Props) => {
  const [hasResponded, setHasResponded] = useState(false);
  const rows = previewRows ?? getPageUpdateChangeRows(args);
  const visibleRows = rows.slice(0, MAX_PREVIEW_ROWS);
  const remainingCount = rows.length - visibleRows.length;

  const respond = (action: () => void | Promise<void>) => {
    setHasResponded(true);
    void Promise.resolve(action()).catch(() => {
      setHasResponded(false);
    });
  };

  return (
    <ApprovalCard>
      <ApprovalCardHeader>
        <EditIcon width={16} height={16} />
        <ApprovalCardTitle>
          The agent wants to update this page
        </ApprovalCardTitle>
      </ApprovalCardHeader>
      <ApprovalCardBody>
        {visibleRows.length > 0 && (
          <ChangesGrid>
            {visibleRows.map((row) => (
              <Fragment key={row.field}>
                <Typography
                  sx={{
                    fontSize: fontSize('md'),
                    fontWeight: fontWeight('medium'),
                    color: 'content.tertiary',
                  }}
                >
                  {row.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: fontSize('md'),
                    wordBreak: 'break-word',
                  }}
                >
                  {row.value}
                </Typography>
              </Fragment>
            ))}
          </ChangesGrid>
        )}
        {remainingCount > 0 && (
          <Typography
            sx={{ fontSize: fontSize('sm'), color: 'content.tertiary' }}
          >
            +{remainingCount} more
          </Typography>
        )}
        <Actions>
          <Button
            variant="outlined"
            size="extraSmall"
            disabled={hasResponded || isDisabled}
            onClick={() => respond(onReject)}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            size="extraSmall"
            disabled={hasResponded || isDisabled}
            onClick={() => respond(onApply)}
          >
            Approve
          </Button>
        </Actions>
      </ApprovalCardBody>
    </ApprovalCard>
  );
};

export default PageUpdateApprovalCard;
